import React, { ReactNode } from "react";

import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  Observable,
  ApolloLink,
  ServerError,
  HttpLink,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { relayStylePagination } from "@apollo/client/utilities";

// generated by Fragment Matcher plugin
import introspectionResult from "graphql/introspection.generated";

import * as Sentry from "@sentry/browser";

import {
  setLatestClientVersion,
  setLatestEnvHash,
} from "utils/clientVersionTracker";
import { StudentSessionConnection } from "./globalTypes.generated";
import getCSRFToken from "helpers/getCSRFToken";

const errorLink = onError(
  ({ operation, response, graphQLErrors, networkError }) => {
    // If the user is not logged in, force refresh the page to "/", to show
    // a login screen.
    if (networkError && (networkError as ServerError).statusCode === 401) {
      window.location.href = "/";
      // The location redirect is not immediate. To prevent any downstream
      // error handlers from triggering, "cancel" the query by returning an
      // observable that will never complete!
      return new Observable(() => {});
    }

    // Filter out errors that were already reported by the server
    graphQLErrors = graphQLErrors?.filter(
      (error) => error.extensions?.needs_reporting,
    );
    if (!graphQLErrors?.length && !networkError) return;

    // Add scoped report details and send to Sentry
    Sentry.withScope((scope) => {
      let issueTitleDetails = "";
      scope.setExtra("operation", operation);
      scope.setExtra("response", response);
      if (networkError) {
        scope.setExtra("networkError", networkError);
        issueTitleDetails += `${networkError.message}, `;
      }
      if (graphQLErrors) {
        scope.setExtra("graphQlErrors", graphQLErrors);
        graphQLErrors.forEach((graphQLError) => {
          issueTitleDetails += `${graphQLError.message}, `;
        });
      }

      const e = Error(issueTitleDetails);
      const error = networkError as ServerError;
      if (error?.statusCode === 422) {
        // Probably a CSRF failure
        e.name = `Received Status Code ${error.statusCode}`;
      } else {
        e.name = operation.operationName;
      }
      // TODO: ignore expected errors
      Sentry.captureException(e);
    });
  },
);

const headersLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      "X-CSRF-Token": getCSRFToken(),
    },
  }));

  return forward(operation).map((data) => {
    const headers: Headers = operation.getContext().response.headers;

    const alongVersion = headers && headers.get("X-Along-Server-Version");
    if (alongVersion) {
      setLatestClientVersion(alongVersion);
    }

    const alongEnvHash = headers && headers.get("X-Along-Env-Digest");
    if (alongEnvHash) {
      setLatestEnvHash(alongEnvHash);
    }

    return data;
  });
});

const httpLink = new HttpLink({
  uri: "/graphql",
});

const client = new ApolloClient({
  cache: new InMemoryCache({
    possibleTypes: introspectionResult.possibleTypes,
    typePolicies: {
      Session: {
        fields: {
          studentSessionsPaginated: relayStylePagination(),
        },
      },
      User: {
        fields: {
          sessions: relayStylePagination(),
        },
      },
      Query: {
        fields: {
          streamPaginated: {
            keyArgs: ["streamType", "sessionId", "userId"],
            merge(
              existing: StudentSessionConnection,
              incoming: StudentSessionConnection,
              { readField, mergeObjects },
            ) {
              let merged: StudentSessionConnection["nodes"] = existing?.nodes
                ? existing.nodes
                : [];
              const idToIndex: Record<string, number> = Object.create(null);
              if (existing?.nodes) {
                existing.nodes.forEach((node, index) => {
                  const id = readField<string>("id", node);
                  if (id) {
                    idToIndex[id] = index;
                  }
                });
              }
              incoming.nodes.forEach((incomingNode) => {
                const id = readField<string>("id", incomingNode);
                if (id) {
                  const index = idToIndex[id];
                  if (typeof index === "number") {
                    // Merge the new node with the existing one
                    merged = merged.map((existingNode, i) =>
                      i === index
                        ? mergeObjects(existingNode, incomingNode)
                        : existingNode,
                    );
                  } else {
                    // First time we've seen this node
                    idToIndex[id] = merged.length;
                    merged = [...merged, incomingNode];
                  }
                }
              });
              return {
                ...incoming,
                nodes: merged,
              };
            },
          },
        },
      },
    },
  }),
  link: from([errorLink, headersLink, httpLink]),
  defaultOptions: {
    // N.B. GraphQL hooks useQuery uses watchQuery behind the scene.
    // https://www.apollographql.com/docs/react/api/core/ApolloClient/#example-defaultoptions-object
    // https://www.apollographql.com/docs/react/data/queries/#supported-fetch-policies
    watchQuery: {
      fetchPolicy: "network-only",
    },
  },
});

export default function ConnectionsProvider(props: { children: ReactNode }) {
  return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
}
